import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TrendIconEnum } from '@/components/atoms/TrendIcon/TrendIconUtils';
import {
    describeTrend,
    type TrendDirection,
    trendHelpText,
} from '@/components/molecules/OverviewValueWithTrendTooltip/describeTrend';
import settings from '@/config/settings';
import type { WelfareOverview } from '@/domain/client';
import type { DailyWelfareData, WelfareData, WelfareMetrics } from '@/domain/welfare/types';
import { formatFractionAsPercentageByLocale, formatNumberByLocale } from '@/utils/localeFormatters';

interface WelfareSummaryInput {
    cageWelfareOverview: WelfareOverview | undefined;
    cageSpeed: number | undefined;
    fishHealthData?: WelfareData;
}

export interface WelfareSummary {
    woundsInfoText: string;
    woundsInfoTooltip: string;
    woundsInfoTrend: TrendIconEnum;
    swimSpeedInfoText: string;
    lastFishHealthDay: DailyWelfareData | null;
    lastEstimate: WelfareMetrics | null;
}

/** The welfare page's headline figures: wound rate and trend, swim speed, latest estimate. */
// The summary shows no arrow while stable or without data, as it always has.
const WOUND_TREND_ICON: Record<TrendDirection, TrendIconEnum> = {
    noData: TrendIconEnum.TrendNone,
    stable: TrendIconEnum.TrendNone,
    growing: TrendIconEnum.TrendNegativeUp,
    decreasing: TrendIconEnum.TrendPositiveDown,
};

export function useWelfareSummary(input: WelfareSummaryInput): WelfareSummary {
    const { t, i18n } = useTranslation();
    const { cageWelfareOverview, cageSpeed, fishHealthData } = input;

    const [woundsInfoText, setWoundsInfoText] = useState('');
    const [woundsInfoTooltip, setWoundsInfoTooltip] = useState('');
    const [woundsInfoTrend, setWoundsInfoTrend] = useState(TrendIconEnum.TrendNone);

    const [swimSpeedInfoText, setSwimSpeedInfoText] = useState('');

    const [lastFishHealthDay, setLastFishHealthDay] = useState<DailyWelfareData | null>(null);
    const [lastEstimate, setLastEstimate] = useState<WelfareMetrics | null>(null);

    const setupWelfareDataFromClientOverview = useCallback(() => {
        if (cageSpeed) {
            setSwimSpeedInfoText(
                `${formatNumberByLocale(cageSpeed, i18n.language, 2)} ${t('common.labels.swimspeed')}`
            );
        } else {
            setSwimSpeedInfoText('-');
        }

        if (cageWelfareOverview) {
            const welfareWoundInfo = Number.isNaN(cageWelfareOverview.woundRate)
                ? ''
                : formatFractionAsPercentageByLocale(
                      cageWelfareOverview.woundRate,
                      i18n.language,
                      1
                  );
            const welfareWoundGrowth = Number.isNaN(cageWelfareOverview.woundSlope)
                ? '---'
                : formatFractionAsPercentageByLocale(
                      cageWelfareOverview.woundSlope,
                      i18n.language,
                      0
                  );

            setWoundsInfoText(welfareWoundInfo);

            // The shared reading: a NaN slope is stable rather than no text, and a stable trend
            // clears an arrow left from an earlier reading.
            const direction = describeTrend(
                cageWelfareOverview.woundRate,
                cageWelfareOverview.woundSlope,
                settings.woundGrowthLimit
            );
            setWoundsInfoTrend(WOUND_TREND_ICON[direction]);
            setWoundsInfoTooltip(trendHelpText(direction, welfareWoundGrowth, t));
        } else {
            setWoundsInfoText('-');
            setWoundsInfoTooltip(t('common.labels.noDataFound'));
        }
    }, [cageWelfareOverview, cageSpeed, t, i18n.language]);

    useEffect(() => {
        setupWelfareDataFromClientOverview();
    }, [setupWelfareDataFromClientOverview]);

    const setupWelfareDataFromFishHealthData = useCallback(() => {
        if (fishHealthData) {
            const lastDay = fishHealthData.days.findLast((x) => (x.count ?? 0) > 0);
            if (lastDay) {
                setLastFishHealthDay(lastDay);
            }
            if (fishHealthData.lastEstimate) {
                setLastEstimate(fishHealthData.lastEstimate);
            }
        }
    }, [fishHealthData]);

    useEffect(() => {
        setupWelfareDataFromFishHealthData();
    }, [setupWelfareDataFromFishHealthData]);

    return {
        woundsInfoText,
        woundsInfoTooltip,
        woundsInfoTrend,
        swimSpeedInfoText,
        lastFishHealthDay,
        lastEstimate,
    };
}
